/**
 * VerifiedLeaderboard — public, indexable leaderboard of the most-viewed
 * certified broadcasts. Deep-links every row to its /verify/{cn} page,
 * building an internal-link network Google can crawl.
 *
 * Route: /verified/:period (this-month | this-week | all-time)
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, TrendingUp, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

const PERIODS = [
  { id: "this-week",  label: "This week"  },
  { id: "this-month", label: "This month" },
  { id: "all-time",   label: "All time"   },
];

function applyLeaderboardSEO(period, count) {
  if (typeof document === "undefined") return;
  const pretty = PERIODS.find((p) => p.id === period)?.label || "This month";
  const title = `Top ${count || 10} verified radio-ad broadcasts — ${pretty} — WaveScorePro`;
  const desc = `Live leaderboard of the most-viewed independently-verified radio ad broadcasts across West Africa. Every entry links to a page you can re-hash for tamper-proof audio evidence.`;
  document.title = title;
  const setMeta = (attr, value, content) => {
    let el = document.head.querySelector(`meta[${attr}="${value}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, value);
      el.setAttribute("data-wsp-managed", "1");
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  const setLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", rel);
      el.setAttribute("data-wsp-managed", "1");
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
  };
  setMeta("name", "description", desc);
  setMeta("property", "og:type", "website");
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:url", `${window.location.origin}/verified/${period}`);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", desc);
  setLink("canonical", `${window.location.origin}/verified/${period}`);
}

export default function VerifiedLeaderboard() {
  const { period: rawPeriod } = useParams();
  const period = PERIODS.find((p) => p.id === rawPeriod) ? rawPeriod : "this-month";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/verified/leaderboard?period=${period}&limit=25`)
      .then((r) => {
        setRows(r.data.rows || []);
        applyLeaderboardSEO(period, r.data.rows?.length || 10);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    return () => {
      document.title = "WaveScorePro";
      document.head.querySelectorAll('[data-wsp-managed="1"]').forEach((el) => el.remove());
    };
  }, [period]);

  return (
    <div data-testid="verified-leaderboard" style={{ minHeight: "100vh", background: T.bg, padding: "40px 24px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 12,
            color: T.greenAccent, fontSize: 12, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase" }}>
            <TrendingUp size={14} /> Verified broadcasts leaderboard
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: T.navy, margin: 0, lineHeight: 1.1 }}>
            The most-viewed verified radio-ad broadcasts
          </h1>
          <p style={{ color: T.muted, fontSize: 15, marginTop: 8, maxWidth: 640 }}>
            Every row deep-links to a certificate page where anyone — no login — can
            re-hash the audio evidence in their own browser and prove it wasn&apos;t swapped.
          </p>
        </div>

        <div data-testid="leaderboard-period-tabs" style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {PERIODS.map((p) => (
            <Link
              key={p.id}
              data-testid={`leaderboard-tab-${p.id}`}
              to={`/verified/${p.id}`}
              style={{
                padding: "8px 16px", borderRadius: 999,
                background: period === p.id ? T.navy : "#F1F5F9",
                color: period === p.id ? "#fff" : T.navy,
                fontSize: 13, fontWeight: 700, textDecoration: "none",
                border: `1px solid ${period === p.id ? T.navy : T.border}`,
              }}>
              {p.label}
            </Link>
          ))}
        </div>

        <div style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
          {loading && <div style={{ padding: 40, textAlign: "center", color: T.muted }}>Loading…</div>}
          {!loading && rows.length === 0 && (
            <div data-testid="leaderboard-empty" style={{ padding: 60, textAlign: "center", color: T.muted }}>
              No certified broadcasts in this window yet. Check back soon.
            </div>
          )}
          {!loading && rows.map((r, i) => {
            const isFp = r.issuance_method === "fingerprint";
            const isManual = r.issuance_method === "manual";
            // F-101 leaderboard-chip fix — 3-way branch. Manual attestations
            // must NEVER wear the "Broadcast-stream" chip that describes an
            // independent stream capture.
            const chipLabel = isFp ? "Audio-fingerprint"
                              : isManual ? "Manual attestation"
                              : "Broadcast-stream";
            const chipColor = isFp ? "#4338CA"
                              : isManual ? "#B45309"
                              : "#0369A1";
            return (
              <Link
                key={r.certificate_number}
                data-testid={`leaderboard-row-${i}`}
                to={`/verify/${r.certificate_number}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 1fr auto 32px",
                  gap: 16, alignItems: "center",
                  padding: "16px 20px",
                  borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : "none",
                  textDecoration: "none", color: "inherit",
                  background: i === 0 ? "linear-gradient(90deg,#F0FDFA 0%,#fff 40%)" : "#fff",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#F8FAFC"}
                onMouseLeave={(e) => e.currentTarget.style.background = i === 0 ? "linear-gradient(90deg,#F0FDFA 0%,#fff 40%)" : "#fff"}>
                <div style={{ fontSize: 20, fontWeight: 800, color: i < 3 ? T.greenAccent : T.muted, textAlign: "center" }}>
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.navy, marginBottom: 3 }}>
                    {r.ad_title || "—"}
                    {r.client_name && <span style={{ color: T.muted, fontWeight: 400 }}> · {r.client_name}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: T.muted, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span>{r.station_name}</span>
                    <span>·</span>
                    <span data-testid={`leaderboard-chip-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 4,
                      color: chipColor, fontWeight: 700 }}>
                      <BadgeCheck size={12} /> {chipLabel}
                    </span>
                    <span>·</span>
                    <span>{(r.view_count || 0).toLocaleString()} views</span>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.greenAccent }}>
                    {(r.estimated_total_reach || 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    listeners
                  </div>
                </div>
                <ArrowRight size={16} color={T.muted} />
              </Link>
            );
          })}
        </div>

        <div style={{ marginTop: 24, padding: 20, background: "#F0FDFA", border: `1px solid #99F6E4`,
          borderRadius: 10, fontSize: 13, color: T.navy }}>
          <b>What&apos;s a &ldquo;verified broadcast&rdquo;?</b> Every certificate on this page comes with a
          downloadable audio clip and a SHA-256 hash. You can re-hash the clip in your browser
          and prove the certificate points at the exact audio we captured. No black-box reports.
          {" "}<Link to="/methodology" style={{ color: T.greenAccent, fontWeight: 700 }}>Read the methodology →</Link>
        </div>
      </div>
    </div>
  );
}
